import { StopRenderJobArgs } from "@/backend/base";
import { RenderPipelineArgs } from "@/core/common/types/render-args";
import {
  isUserRenderJobState,
  UserRenderJobState,
  UserRenderJobStatus,
} from "@/core/common/types/render-job";
import { debugError, debugLog } from "@/core/utils/print-utilts";
import { collection, doc, Firestore, onSnapshot, updateDoc } from "firebase/firestore";
import { FIREBASE_ID_TOKEN_HEADER, getFirebaseIdTokenHeader } from "./firebase-id-token-header";
import { renderOutputDataUrl } from "./render-output";
import { inlineRenderInputs } from "./render-inline-inputs";
import {
  confirmRenderAdmissionRequest,
  getRenderAdmissionRequest,
  renderAdmissionFingerprint,
  RenderAdmissionError,
} from "./render-admission-request";

const renderQueueProducerApiUrl = import.meta.env.VITE_RENDER_QUEUE_PRODUCER_API_URL;
const SECRET_API_KEY = import.meta.env.VITE_RENDER_QUEUE_PRODUCER_API_KEY;
const protocol = import.meta.env.VITE_RENDER_QUEUE_PROTOCOL_VERSION || "1";
const userRenderStateCollectionName = protocol === "2" ? "userRenderStateV2" : "userRenderState";
const userRenderJobCollectionName = "renderJobs";
const pendingAdmissions = new Map<string, Promise<AddRenderJobResponse>>();

interface AddRenderJobArgs {
  userId: string;
  publicTeamId: string;
  numOutputs?: number;
  renderPipelineArgs: RenderPipelineArgs;
}

type AddRenderJobResponse =
  | {
      ok: false;
      message: string;
    }
  | {
      ok: true;
      jobIds: string[];
    };

function queueEndpoint() {
  if (protocol !== "1" && protocol !== "2") {
    throw new RenderAdmissionError("Render configuration is unavailable.");
  }
  const url = new URL(renderQueueProducerApiUrl);
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash) {
    throw new RenderAdmissionError("Render configuration is unavailable.");
  }
  return url.href;
}

function queueFailure(status: number) {
  if (status === 401) {
    return "Please sign in again before rendering.";
  }
  if (status === 403) {
    return "Your current team does not have permission to render.";
  }
  if (status === 413) {
    return "This render request is too large.";
  }
  if (status === 429) {
    return "Render capacity is busy. Please retry this request shortly.";
  }
  return "The render could not be confirmed. Retry the same request to check its status.";
}

async function queueResponse(response: Response) {
  if (!response.ok) {
    await response.body?.cancel();
    throw new RenderAdmissionError(queueFailure(response.status));
  }
  if (!response.body) {
    throw new RenderAdmissionError("Render confirmation is unavailable.");
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    let chunk = await reader.read();
    while (!chunk.done) {
      const value = chunk.value;
      size += value.byteLength;
      if (size > 16 * 1024) {
        throw new RenderAdmissionError("Render confirmation is unavailable.");
      }
      chunks.push(value);
      chunk = await reader.read();
    }
    const bytes = new Uint8Array(size);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

async function submitV2(args: AddRenderJobArgs, body: string, fingerprint: string) {
  const identityHeader = await getFirebaseIdTokenHeader(args.userId);
  if (!identityHeader[FIREBASE_ID_TOKEN_HEADER]) {
    return { ok: false as const, message: "Please sign in again before starting a render." };
  }
  const requestId = await getRenderAdmissionRequest(fingerprint);
  const response = await fetch(queueEndpoint(), {
    method: "POST",
    redirect: "error",
    credentials: "omit",
    cache: "no-store",
    signal: AbortSignal.timeout(30_000),
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": requestId,
      flair_auth_version: "v2_collaborate",
      flair_auth_public_team_id: args.publicTeamId,
      NumOutputs: String(args.numOutputs ?? 1),
      ...identityHeader,
    },
    body,
  });
  const data = await queueResponse(response);
  const ids = data?.jobIds;
  if (
    data?.protocolVersion !== 2 ||
    !Array.isArray(ids) ||
    ids.length !== (args.numOutputs ?? 1) ||
    ids.some(
      (id, index) =>
        typeof id !== "string" ||
        !/^[a-f0-9]{64}_[0-3]$/.test(id) ||
        id !== ids[0].slice(0, 64) + "_" + index,
    )
  ) {
    throw new RenderAdmissionError(
      "Render confirmation is unavailable. Retry this request to check its status.",
    );
  }
  // An uncertain response/storage failure keeps the request ID until it expires.
  await confirmRenderAdmissionRequest(fingerprint, requestId);
  return { ok: true as const, jobIds: ids as string[] };
}

function getUserRenderJobsCollectionRef({
  firestore,
  userId,
}: {
  firestore: Firestore;
  userId: string;
}) {
  return collection(
    doc(collection(firestore, userRenderStateCollectionName), userId),
    userRenderJobCollectionName,
  );
}

function getUserRenderJobDocRef({
  firestore,
  userId,
  jobId,
}: {
  firestore: Firestore;
  userId: string;
  jobId: string;
}) {
  return doc(
    getUserRenderJobsCollectionRef({
      firestore,
      userId,
    }),
    jobId,
  );
}

export class RenderStateManager {
  private firestore: Firestore;

  constructor({ firestore }: { firestore: Firestore }) {
    this.firestore = firestore;
  }

  async startRenderJob({
    userId,
    publicTeamId,
    numOutputs = 1,
    renderPipelineArgs,
  }: AddRenderJobArgs): Promise<AddRenderJobResponse> {
    try {
      queueEndpoint();
      if (!/^[a-zA-Z0-9_-]{1,512}_tpuid$/.test(publicTeamId)) {
        return { ok: false, message: "Select a team before starting a render." };
      }

      if (protocol === "2") {
        if (
          !Number.isInteger(numOutputs) ||
          numOutputs < 1 ||
          numOutputs > 4 ||
          publicTeamId.length > 512
        ) {
          return { ok: false, message: "Request one to four outputs for the selected team." };
        }
        const currentIdentity = await getFirebaseIdTokenHeader(userId);
        if (!currentIdentity[FIREBASE_ID_TOKEN_HEADER]) {
          return { ok: false, message: "Please sign in again before starting a render." };
        }
        const body = JSON.stringify(await inlineRenderInputs(renderPipelineArgs));
        if (new TextEncoder().encode(body).byteLength > 8 * 1024 * 1024) {
          return { ok: false, message: "This render request is too large." };
        }
        const args = { userId, publicTeamId, numOutputs, renderPipelineArgs };
        const fingerprint = await renderAdmissionFingerprint({
          endpoint: queueEndpoint(),
          userId,
          publicTeamId,
          numOutputs,
          body,
        });
        const pending = pendingAdmissions.get(fingerprint);
        if (pending) {
          return await pending;
        }
        if (pendingAdmissions.size >= 2) {
          return { ok: false, message: "Wait for the current render requests to be confirmed." };
        }
        const admission = submitV2(args, body, fingerprint);
        pendingAdmissions.set(fingerprint, admission);
        try {
          return await admission;
        } finally {
          if (pendingAdmissions.get(fingerprint) === admission) {
            pendingAdmissions.delete(fingerprint);
          }
        }
      }

      const identityHeader = await getFirebaseIdTokenHeader();
      if (!identityHeader[FIREBASE_ID_TOKEN_HEADER]) {
        return {
          ok: false,
          message: "Please sign in again before starting a render.",
        };
      }

      const response = await fetch(renderQueueProducerApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SECRET_API_KEY}`,
          UserId: userId,
          flair_auth_version: "v2_collaborate",
          flair_auth_public_team_id: publicTeamId,
          NumOutputs: numOutputs.toString(),
          ...identityHeader,
        },
        body: JSON.stringify(renderPipelineArgs),
      });

      if (!response.ok) {
        const message = await response.text();

        debugError("Render admission was rejected.");
        return {
          ok: false,
          message,
        };
      }

      const data = await response.json();

      const jobIds = data?.jobIds;
      if (Array.isArray(jobIds)) {
        return {
          ok: true,
          jobIds,
        };
      }

      return {
        ok: false,
        message: "Cannot start render job.",
      };
    } catch (error) {
      if (protocol === "2") {
        if (error instanceof RenderAdmissionError) {
          return { ok: false, message: error.message };
        }
        return {
          ok: false,
          message: "The render could not be confirmed. Retry the same request to check its status.",
        };
      }
      debugError("Render admission is unavailable.");
    }

    return {
      ok: false,
      message: "Unknown error when starting render job.",
    };
  }

  async getPrivateRenderOutput({
    userId,
    jobId,
  }: {
    userId: string;
    jobId: string;
  }): Promise<string | undefined> {
    queueEndpoint();
    if (protocol === "1") return undefined;
    if (!/^[a-f0-9]{64}_[0-3]$/.test(jobId)) throw new RenderAdmissionError("Invalid render job.");
    const identityHeader = await getFirebaseIdTokenHeader(userId);
    if (!identityHeader[FIREBASE_ID_TOKEN_HEADER])
      throw new RenderAdmissionError("Sign in again to read this render.");
    const signal = AbortSignal.timeout(30_000);
    const response = await fetch(queueEndpoint(), {
      method: "POST",
      redirect: "error",
      credentials: "omit",
      cache: "no-store",
      signal,
      headers: {
        "Content-Type": "application/json",
        "X-Render-Action": "download",
        ...identityHeader,
      },
      body: JSON.stringify({ jobId }),
    });
    // Protocol 2 never falls back to the legacy default bucket or a path-only read.
    return renderOutputDataUrl(response, jobId, signal);
  }

  onRenderJobUpdate({
    userId,
    jobId,
    callback,
    onError,
  }: {
    userId: string;
    jobId: string;
    callback: (renderJob: UserRenderJobState | null) => void;
    onError?: (error: Error) => void;
  }) {
    return onSnapshot(
      getUserRenderJobDocRef({
        firestore: this.firestore,
        userId,
        jobId,
      }),
      (snapshot) => {
        const data = snapshot.data();

        // A cache miss is not proof that the server-owned status is absent.
        if (snapshot.metadata.fromCache && !snapshot.exists()) {
          return;
        }

        if (
          isUserRenderJobState(data) &&
          data.id === jobId &&
          Object.values(UserRenderJobStatus).includes(data.status) &&
          (data.gcp_storage_paths === undefined ||
            (Array.isArray(data.gcp_storage_paths) &&
              data.gcp_storage_paths.length <= 4 &&
              data.gcp_storage_paths.every((path) => typeof path === "string")))
        ) {
          callback(data);
        } else {
          onError?.(new RenderAdmissionError("Render status is unavailable."));
          if (!onError) {
            callback(null);
          }
        }
      },
      () => {
        onError?.(
          new RenderAdmissionError(
            "Render status could not be read. Check your sign-in and team access.",
          ),
        );
        if (!onError) {
          callback(null);
        }
      },
    );
  }

  async stopRenderJob({ userId, jobId }: StopRenderJobArgs) {
    if (protocol !== "1" && protocol !== "2") {
      throw new RenderAdmissionError("Render configuration is unavailable.");
    }
    if (protocol === "2") {
      if (!/^[a-f0-9]{64}_[0-3]$/.test(jobId)) {
        throw new RenderAdmissionError("Invalid render job.");
      }
      const identityHeader = await getFirebaseIdTokenHeader(userId);
      if (!identityHeader[FIREBASE_ID_TOKEN_HEADER]) {
        throw new RenderAdmissionError("Sign in again to stop this render.");
      }
      const response = await fetch(queueEndpoint(), {
        method: "POST",
        redirect: "error",
        credentials: "omit",
        cache: "no-store",
        signal: AbortSignal.timeout(30_000),
        headers: {
          "Content-Type": "application/json",
          "X-Render-Action": "cancel",
          ...identityHeader,
        },
        body: JSON.stringify({ jobId }),
      });
      const data = await queueResponse(response);
      if (data.stopped !== true) {
        throw new RenderAdmissionError("This render could not be stopped. Retry stopping it.");
      }
      return;
    }
    try {
      debugLog(`Stop user ${userId} render job ${jobId}`);

      await updateDoc(
        getUserRenderJobDocRef({
          firestore: this.firestore,
          userId,
          jobId,
        }),
        {
          status: UserRenderJobStatus.Stopped,
        },
      );
    } catch (error) {
      debugError(`Error cancelling user ${userId} job ${jobId}: `, error);
    }
  }
}
